/**
 * Member Workflows tab.
 *
 * Lists every tco_workflow_instance the active org can see for this member
 * (direct + hub-network) via /api/members/{id}/workflow-instances. Each row
 * shows the template name, trigger type, status, step progress, and the
 * linked service_referral if the workflow created one.
 */
import React, { useEffect, useState } from 'react';
import { authFetchJson } from '../../lib/authFetch';
import { Activity, CheckCircle, Clock, AlertTriangle, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
// TRUS-757 F5: steps (incl. referral_status_gate) with the status they wait for.
import { WorkflowGateSteps } from '@/components/referral-inbound/ReferralWorkflowGates';

interface WorkflowInstance {
  id: number;
  template_id: number | null;
  template_name: string | null;
  template_type: string | null;
  cbo_id: number | null;
  enrollment_id: number | null;
  care_plan_id: number | null;
  trigger_type: string | null;
  status: string | null;
  current_step: number | null;
  total_steps: number | null;
  last_outreach_at: string | null;
  last_response_at: string | null;
  outcome: string | null;
  service_referral_id: number | null;
  referral_number: string | null;
  service_category_code: string | null;
  member_request_type: string | null;
  program_id: number | null;
  program_name: string | null;
  created_at: string | null;
}

const STATUS_COLOR: Record<string, string> = {
  in_progress:       'bg-blue-100 text-blue-700',
  waiting_response:  'bg-amber-100 text-amber-700',
  completed:         'bg-emerald-100 text-emerald-700',
  succeeded:         'bg-emerald-100 text-emerald-700',
  failed:            'bg-red-100 text-red-700',
  cancelled:         'bg-gray-100 text-gray-700',
  pending:           'bg-gray-100 text-gray-600',
};

export const MemberWorkflowsTab: React.FC<{ memberId: number }> = ({ memberId }) => {
  const { t } = useAppTranslation();
  const [rows, setRows] = useState<WorkflowInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    authFetchJson<WorkflowInstance[]>(`/api/members/${memberId}/workflow-instances`)
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(e => setError(e?.message || 'Failed to load workflow instances'))
      .finally(() => setLoading(false));
  }, [memberId]);

  if (loading) return <div className="p-6 text-sm text-gray-500">{t('members.loadingWorkflows', 'Loading workflows…')}</div>;
  if (error)   return <div className="p-6 text-sm text-red-600">{error}</div>;
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
        <Activity className="mb-2 h-5 w-5 text-gray-400" />
        {t('members.noWorkflowInstances', 'No workflow instances yet. Workflows fire automatically on enrollment, discharge, intent classification, or care-plan intervention. Manual triggers can be created from')}{' '}
        <strong>{t('members.careCoordinationHubTemplates', 'Care Coordination Hub → Templates')}</strong>.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{t('members.workflows', 'Workflows')} ({rows.length})</h3>
        <p className="text-xs text-gray-500">
          {t('members.workflowInstancesSubtitle', 'tco_workflow_instances tied to this member, including hub-network reach.')}
        </p>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left">{t('members.template', 'Template')}</th>
              <th className="px-4 py-2 text-left">{t('members.trigger', 'Trigger')}</th>
              <th className="px-4 py-2 text-left">{t('members.progress', 'Progress')}</th>
              <th className="px-4 py-2 text-left">{t('members.status', 'Status')}</th>
              <th className="px-4 py-2 text-left">{t('members.referral', 'Referral')}</th>
              <th className="px-4 py-2 text-left">{t('members.program', 'Program')}</th>
              <th className="px-4 py-2 text-left">{t('members.created', 'Created')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => {
              const statusColor = STATUS_COLOR[(r.status || '').toLowerCase()] || 'bg-gray-100 text-gray-700';
              return (
                <React.Fragment key={r.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-2 align-top">
                    <div className="font-medium text-gray-900">{r.template_name || `Template #${r.template_id}`}</div>
                    {r.template_type && (
                      <div className="text-xs text-gray-500">{r.template_type.replace(/_/g, ' ')}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 align-top text-xs text-gray-700">
                    {r.trigger_type ? r.trigger_type.replace(/_/g, ' ') : '—'}
                  </td>
                  <td className="px-4 py-2 align-top text-xs text-gray-700">
                    {r.current_step != null && r.total_steps != null
                      ? `${r.current_step}/${r.total_steps}`
                      : '—'}
                    <button
                      type="button"
                      className="ml-2 inline-flex items-center gap-0.5 text-indigo-700 hover:underline"
                      aria-expanded={expandedId === r.id}
                      onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                    >
                      {expandedId === r.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      {t('members.workflowSteps', 'Steps')}
                    </button>
                  </td>
                  <td className="px-4 py-2 align-top">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColor}`}>
                      {r.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-top text-xs">
                    {r.referral_number ? (
                      <span className="inline-flex items-center gap-1 font-mono text-indigo-700">
                        <ExternalLink className="h-3 w-3" />
                        {r.referral_number}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 align-top text-xs text-gray-700">
                    {r.program_name || (r.program_id ? `Program #${r.program_id}` : '—')}
                  </td>
                  <td className="px-4 py-2 align-top text-xs text-gray-500">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
                {expandedId === r.id && (
                  <tr>
                    <td colSpan={7} className="bg-gray-50 px-6 py-2">
                      <WorkflowGateSteps instanceId={r.id} instanceStatus={r.status} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemberWorkflowsTab;
